/**
 * First-party site analytics (docs/SITE-ANALYTICS.md): the sign-up funnel and page views with heatmap averages. Pure parts only.
 * Owner, 2026-10-05: "Let's own the whole data pipeline through sign up and monitor dropoff and heatmap averages on page views."
 * No third party (no PostHog), no cookies, no ids, no fingerprinting, no IP kept. Counters only:
 *  - The funnel is counted per day, state, source and utm_source. It is reported as ratios of those counts over the same days
 *    (cohort ratios), never as one person's path: nothing joins a visit to a sign-up.
 *  - Page views are aggregated the moment they arrive into per-page, per-day, per-viewport buckets: views, a scroll-depth histogram,
 *    clicks per (5% of the width) x (50 px) cell and per stable element name. A raw event is never stored.
 *  - Scope: the landing site's pages and the sign-in and Setup screens of the web app ONLY (PAGE_RULES below). Never a screen
 *    after onboarding (check-ins, sick reports, logs, anything health) on web or phone.
 */
import { STATE_INDEX } from "./states/bundles/eager-facts.ts";
import { SUPPORTED_STATES, type UsState } from "./states/types.ts";
import { dayIn } from "./referrals.ts";
import { TRIAL_DAYS } from "./billing.ts";

/* ─────────────── the funnel ─────────────── */

export const FUNNEL_STEPS = ["visit", "signup", "business", "checkout", "trial", "paid", "month3"] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];
export const FUNNEL_STEP_LABEL: Record<FunnelStep, string> = {
  visit: "Visited the site", signup: "Signed up", business: "Set up a business", checkout: "Opened checkout",
  trial: "Started the free days", paid: "Paid the first month", month3: "Still paying in month 3",
};
/** The steps a business takes after it exists; each is counted once per business (the server keeps which ones it passed). */
export const ORG_STEPS = ["business", "checkout", "trial", "paid", "month3"] as const;
export type OrgStep = (typeof ORG_STEPS)[number];

/** How a visitor arrived, as a bucket: never the referring address itself. "none" = not through the site's sign-up link. */
export const VISIT_SOURCES = ["google", "find", "direct", "other", "none"] as const;
export type VisitSource = (typeof VISIT_SOURCES)[number];
export const VISIT_SOURCE_LABEL: Record<VisitSource, string> = {
  google: "Google", find: "A county /find/ page", direct: "Direct (no referrer)", other: "Another site", none: "Not through the site",
};

/** The days the reports use: Utah's (the business is in Salt Lake City). */
export const REPORT_TZ = "America/Denver";
export const reportDay = (at: Date | string): string => dayIn(at, REPORT_TZ);

export const cleanVisitSource = (v: unknown): VisitSource => (typeof v === "string" && (VISIT_SOURCES as readonly string[]).includes(v) ? (v as VisitSource) : "none");
/** utm_source only, lowercased, short and plain ("newsletter", "fb"); anything else is dropped. */
export function cleanUtm(v: unknown): string {
  if (typeof v !== "string") return "";
  const s = v.trim().toLowerCase();
  return /^[a-z0-9][a-z0-9._-]{0,39}$/.test(s) && s !== "other" ? s : ""; // "other" is the server's overflow bucket (security run-10)
}
export const cleanState = (v: unknown): UsState | "" => (typeof v === "string" && (SUPPORTED_STATES as readonly string[]).includes(v.toUpperCase()) ? (v.toUpperCase() as UsState) : "");

const SLUG_STATE = new Map(Object.entries(STATE_INDEX).map(([code, s]) => [s.slug, code as UsState]));
/** The state a landing page is about, from its path: /utah/..., /find/nevada/..., /california/blog/... Else "". */
export function stateOfPath(path: string): UsState | "" {
  const seg = path.split("/").filter(Boolean);
  const first = seg[0] === "find" || seg[0] === "states" ? seg[1] : seg[0];
  return (first && SLUG_STATE.get(first)) || "";
}

/**
 * The source bucket of a visit that ENTERED the site (the referrer is not the site itself): a /find/ county page it landed on,
 * else Google, else direct (no referrer), else another site. `ownHost` is the site's host; any *.cleancleaver.com counts as own.
 * Returns null for a page reached from the site itself (not an entry). The same rule runs in the browser (apps/landing/pv.js).
 */
export function entrySource(referrer: string, ownHost: string, path: string): VisitSource | null {
  let host = "";
  try { host = referrer ? new URL(referrer).hostname.toLowerCase() : ""; } catch { host = ""; }
  const own = ownHost.toLowerCase();
  if (host && (host === own || host === "cleancleaver.com" || host.endsWith(".cleancleaver.com"))) return null;
  if (/^\/find\/[a-z-]+\/[a-z0-9-]+\//.test(path)) return "find";
  if (!host) return "direct";
  if (/(^|\.)google\.[a-z.]{2,6}$/.test(host)) return "google";
  return "other";
}

export interface FunnelCount { day: string; step: FunnelStep; state: string; source: VisitSource; utm: string; n: number }
export interface FunnelFilter { from: string; to: string; state?: string; source?: string }
/**
 * How long after the step before a step usually happens, in days. A step is compared with the step before over the SAME number of
 * days shifted back by this lag, so a first payment is set against the free days that started 25 days earlier (TRIAL_DAYS), and
 * month 3 against the first payments two months earlier. The other steps happen within minutes of each other: the same days.
 */
export const STEP_LAG_DAYS: Record<FunnelStep, number> = { visit: 0, signup: 0, business: 0, checkout: 0, trial: 0, paid: TRIAL_DAYS, month3: 61 };
/** How far back the counts must reach to compare every step (the server reads this many days before `from`). */
export const FUNNEL_LOOKBACK_DAYS = Object.values(STEP_LAG_DAYS).reduce((a, b) => a + b, 0);

export interface FunnelStepRow {
  step: FunnelStep; label: string; n: number;
  /** The step before, counted over [prevFrom, prevTo] (the range shifted back by this step's lag); null for the first step. */
  prev: { n: number; from: string; to: string } | null;
  /** This step's count over the step before's, as a percent with one decimal; null when the step before is 0. */
  fromPrev: number | null;
  /** 100 - fromPrev: the drop-off from the step before. Null above 100% (counts, not people: some came from earlier days). */
  dropOff: number | null;
}
export interface FunnelReport {
  from: string; to: string; state: string; source: string;
  steps: FunnelStepRow[];
  /** Paid in the range over visits in the range shifted back by the free days (the sum of the lags from visit to paid). */
  visitorToPaid: { paid: number; visits: number; visitsFrom: string; visitsTo: string; pct: number | null };
  /** One row per day in the range (zeros included), oldest first. */
  days: { day: string; counts: Record<FunnelStep, number> }[];
  /** What the filters can pick: the states, sources and utm_source values present in the range. */
  states: string[]; sources: VisitSource[]; utms: string[];
}

const pct = (n: number, of: number): number | null => (of > 0 ? Math.round((n / of) * 1000) / 10 : null);
const zero = (): Record<FunnelStep, number> => Object.fromEntries(FUNNEL_STEPS.map((s) => [s, 0])) as Record<FunnelStep, number>;
export const shiftDay = (day: string, days: number) => new Date(Date.parse(`${day}T12:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

export function daysBetween(from: string, to: string, max = 400): string[] {
  const out: string[] = [];
  for (let t = Date.parse(`${from}T12:00:00Z`), end = Date.parse(`${to}T12:00:00Z`); t <= end && out.length < max; t += 86_400_000) out.push(new Date(t).toISOString().slice(0, 10));
  return out;
}

/**
 * The funnel over [from, to] (Utah days), filtered by state ("all" or a code; "" = unknown) and source ("all", a bucket, or
 * "utm:<value>"). Each step's share compares its count with the step before's over the range shifted back by STEP_LAG_DAYS:
 * a cohort ratio of counts, never one person's path. `rows` should reach FUNNEL_LOOKBACK_DAYS before `from`.
 */
export function funnelReport(rows: readonly FunnelCount[], f: FunnelFilter): FunnelReport {
  const state = f.state ?? "all", source = f.source ?? "all";
  const keep = (r: FunnelCount) => (state === "all" || r.state === state) && (source === "all" || (source.startsWith("utm:") ? r.utm === source.slice(4) : r.source === source));
  const kept = rows.filter(keep);
  const count = (step: FunnelStep, from: string, to: string) => kept.reduce((a, r) => (r.step === step && r.day >= from && r.day <= to ? a + r.n : a), 0);
  const inRange = rows.filter((r) => r.day >= f.from && r.day <= f.to);
  const perDay = new Map(daysBetween(f.from, f.to).map((d) => [d, zero()]));
  for (const r of kept) { const d = perDay.get(r.day); if (d) d[r.step] += r.n; }
  const steps = FUNNEL_STEPS.map((step, i): FunnelStepRow => {
    const n = count(step, f.from, f.to);
    if (i === 0) return { step, label: FUNNEL_STEP_LABEL[step], n, prev: null, fromPrev: null, dropOff: null };
    const lag = STEP_LAG_DAYS[step], pFrom = shiftDay(f.from, -lag), pTo = shiftDay(f.to, -lag);
    const prevN = count(FUNNEL_STEPS[i - 1]!, pFrom, pTo);
    const fromPrev = pct(n, prevN);
    return { step, label: FUNNEL_STEP_LABEL[step], n, prev: { n: prevN, from: pFrom, to: pTo }, fromPrev, dropOff: fromPrev === null || fromPrev > 100 ? null : Math.round((100 - fromPrev) * 10) / 10 };
  });
  const back = FUNNEL_STEPS.slice(1, FUNNEL_STEPS.indexOf("paid") + 1).reduce((a, s) => a + STEP_LAG_DAYS[s], 0);
  const vFrom = shiftDay(f.from, -back), vTo = shiftDay(f.to, -back);
  const paid = count("paid", f.from, f.to), visits = count("visit", vFrom, vTo);
  const uniq = <T,>(xs: T[]) => [...new Set(xs)].sort() as T[];
  return {
    from: f.from, to: f.to, state, source, steps,
    visitorToPaid: { paid, visits, visitsFrom: vFrom, visitsTo: vTo, pct: pct(paid, visits) },
    days: [...perDay.entries()].map(([day, counts]) => ({ day, counts })),
    states: uniq(inRange.map((r) => r.state)), sources: uniq(inRange.map((r) => r.source)), utms: uniq(inRange.map((r) => r.utm).filter(Boolean)),
  };
}

/* ─────────────── which pages may be measured ─────────────── */

/**
 * The web app's Setup steps (apps/web/src/screens/Setup.tsx `Key`): measured as "app:setup/<step>". "find" and "match" are the
 * "Is this you?" pair (the first screens in a state with official results; they were sent and refused, so the pilot's first two
 * screens were never counted: CONVERSION-2026-10-13.md C1), "buy" and "buy-card" the site buyer's two screens (screens/BuySetup.tsx),
 * "ready" the screen after "Yes, that's us" (screens/SetupReady.tsx). The older names stay so past days still read.
 */
export const SETUP_STEPS = ["find", "match", "who", "join", "me", "role", "stage", "serving", "type", "name", "faith", "county", "permit", "hours", "menu", "done", "buy", "buy-card", "ready"] as const;
/**
 * First path parts that are never a landing page: the apps and every screen after onboarding (and anything that sounds like
 * health), so a mistaken or forged beacon for them is refused even though the landing pattern below would fit it.
 */
export const NEVER_FIRST = new Set([
  "app", "demo", "admin", "api", "s", "guide", "invite", "allergen-menu", "allergy-order", "join", "kids", "welcome", "workspaces",
  "check-in", "checkin", "checkins", "health", "sick", "sick-report", "report", "reports", "incident", "incidents", "logs", "log",
  "do", "today", "team", "activity", "me", "points", "learn", "paper", "recall", "allergens", "food", "rooms", "clean", "objects",
  "carrying", "more", "settings", "people", "binder", "records", "content", "have", "start", "orgs", "auth", "pv",
]);
/**
 * The path parts of every screen that shows or asks about a person's health (FRAMEWORK T4): the daily check-in, a sick or incident
 * report, and the screens that list people with their check-in outcomes (team, people, activity, me, the shared tablet, the audit
 * trail). Never measured on the site (they are in NEVER_FIRST) and never in the app's own events (core appevents.ts): a screen
 * with any of these parts anywhere in its route sends nothing at all, not even a tap.
 */
export const HEALTH_PATH_PARTS: ReadonlySet<string> = new Set([
  "check-in", "checkin", "checkins", "health", "sick", "sick-report", "report", "reports", "incident", "incidents",
  "team", "people", "activity", "me", "kiosk", "kiosk-lessons", "audit-trail",
]);
/**
 * The allowlist, as route patterns. A page is measured only if one of these fits AND (for a landing path) its first part is not
 * in NEVER_FIRST. Landing paths are directory paths ("/", "/pricing/", "/find/utah/salt-lake/"): no query, no file names.
 */
export const PAGE_RULES: readonly { name: string; pattern: RegExp }[] = [
  { name: "landing page", pattern: /^\/(?:[a-z0-9][a-z0-9-]{0,79}\/){0,5}$/ },
  // "/read": a sign-up from a library guide or a blog article, whose screen names what they read (core readfrom.ts).
  { name: "app sign-in", pattern: /^app:sign-in(?:\/read)?$/ },
  { name: "app setup", pattern: new RegExp(`^app:setup(?:/(?:${SETUP_STEPS.join("|")}))?$`) },
];
export function pageAllowed(page: unknown): page is string {
  if (typeof page !== "string" || page.length > 160) return false;
  const rule = PAGE_RULES.find((r) => r.pattern.test(page));
  if (!rule) return false;
  if (rule.name === "landing page") { const first = page.split("/")[1] ?? ""; if (NEVER_FIRST.has(first)) return false; }
  return true;
}

/* ─────────────── page beacons and their aggregate ─────────────── */

export const VIEWPORTS = ["mobile", "tablet", "desktop"] as const;
export type Viewport = (typeof VIEWPORTS)[number];
export const viewportOf = (width: number): Viewport => (width < 600 ? "mobile" : width < 1024 ? "tablet" : "desktop");
/** x is a percent of the document width, in 5% columns; y is px from the top in 50 px rows, up to 20,000 px. */
export const X_COLS = 20, Y_ROW_PX = 50, Y_MAX_PX = 20_000;
export const SCROLL_STEPS = 11; // 0%, 10%, ... 100%
export const MAX_CLICKS_PER_BEACON = 60;
export const MAX_TARGETS_PER_PAGE = 200;
export const MAX_BEACON_BYTES = 8_192;
/** The stable name of the element clicked: a data-cc name, an element id, or the kind of control. Never its text. */
export const TARGET_PATTERN = /^(?:cc:[a-z0-9][a-z0-9-]{0,39}|#[A-Za-z][A-Za-z0-9_-]{0,40}|signup|signin|a|button|summary|label|img)$/;

export interface Beacon {
  page: string; vp: Viewport;
  /** This beacon opens the page view (the first batch from a page; later batches carry only more clicks). */
  view: boolean;
  /** The view entered the site from outside, with its source bucket (a funnel "visit"); null = reached from the site itself. */
  entry: VisitSource | null;
  utm: string;
  /** Deepest scroll reached, 0..100 in steps of 10; null if not sent. */
  scroll: number | null;
  clicks: { x: number; y: number; t: string | null }[];
}

/** The ONLY keys a beacon may carry. Anything else (a value, text, a URL, an id) makes the whole beacon refused. */
export const BEACON_KEYS = ["p", "vp", "v", "e", "u", "sc", "c"] as const;

const int = (v: unknown, lo: number, hi: number): number | null => (typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi ? v : null);

/** Checks a beacon strictly. Null = refuse it all (nothing is counted from a beacon that carries anything unexpected). */
export function parseBeacon(raw: unknown): Beacon | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (Object.keys(o).some((k) => !(BEACON_KEYS as readonly string[]).includes(k))) return null;
  if (!pageAllowed(o.p)) return null;
  if (!(VIEWPORTS as readonly string[]).includes(o.vp as string)) return null;
  if (o.v !== undefined && typeof o.v !== "boolean") return null;
  const entry = o.e === undefined || o.e === null ? null : (VISIT_SOURCES as readonly string[]).includes(o.e as string) && o.e !== "none" ? (o.e as VisitSource) : undefined;
  if (entry === undefined) return null;
  if (o.u !== undefined && cleanUtm(o.u) !== o.u) return null;
  const scroll = o.sc === undefined ? null : int(o.sc, 0, 100);
  if (o.sc !== undefined && (scroll === null || scroll % 10 !== 0)) return null;
  const list = o.c === undefined ? [] : o.c;
  if (!Array.isArray(list) || list.length > MAX_CLICKS_PER_BEACON) return null;
  const clicks: Beacon["clicks"] = [];
  for (const c of list) {
    if (!Array.isArray(c) || c.length < 2 || c.length > 3) return null;
    const x = int(c[0], 0, 100), y = int(c[1], 0, Y_MAX_PX);
    if (x === null || y === null) return null;
    const t = c[2] === undefined || c[2] === null ? null : typeof c[2] === "string" && TARGET_PATTERN.test(c[2]) ? c[2] : undefined;
    if (t === undefined) return null;
    clicks.push({ x, y, t });
  }
  const view = o.v === true;
  // An entry is only meaningful on the view itself.
  return { page: o.p, vp: o.vp as Viewport, view, entry: view ? entry : null, utm: view && typeof o.u === "string" ? o.u : "", scroll: view ? scroll : null, clicks };
}

/** One page, one day, one viewport class: all that is ever kept about page views. */
export interface PageDay {
  day: string; page: string; vp: Viewport;
  views: number;
  /** scroll[i] = views whose deepest scroll was i*10 %. */
  scroll: number[];
  /** "col:row" -> clicks. */
  clicks: Record<string, number>;
  /** stable element name -> clicks. */
  targets: Record<string, number>;
}
export type PageDelta = Pick<PageDay, "views" | "scroll" | "clicks" | "targets">;

export const cellKey = (x: number, y: number) => `${Math.min(X_COLS - 1, Math.floor(x / (100 / X_COLS)))}:${Math.min(Math.floor(Y_MAX_PX / Y_ROW_PX) - 1, Math.floor(y / Y_ROW_PX))}`;

/** What one beacon adds to its page's day. */
export function pageDelta(b: Beacon): PageDelta {
  const scroll = Array<number>(SCROLL_STEPS).fill(0);
  if (b.view && b.scroll !== null) scroll[b.scroll / 10]! += 1;
  const clicks: Record<string, number> = {}, targets: Record<string, number> = {};
  for (const c of b.clicks) {
    const k = cellKey(c.x, c.y);
    clicks[k] = (clicks[k] ?? 0) + 1;
    if (c.t) targets[c.t] = (targets[c.t] ?? 0) + 1;
  }
  return { views: b.view ? 1 : 0, scroll, clicks, targets };
}

const addCounts = (a: Record<string, number>, b: Record<string, number>, cap = Infinity) => {
  const out = { ...a };
  const had = Object.keys(a).length; // the cap looks at the counts before this delta (as the SQL does)
  for (const [k, v] of Object.entries(b)) {
    const key = k in a || had < cap ? k : "other";
    out[key] = (out[key] ?? 0) + v;
  }
  return out;
};
/** Adds a delta to a page's day (the memory store; Postgres does the same in SQL). Targets past the cap fold into "other". */
export function addPageDelta(a: PageDay, d: PageDelta): PageDay {
  return {
    ...a, views: a.views + d.views,
    scroll: Array.from({ length: SCROLL_STEPS }, (_, i) => (a.scroll[i] ?? 0) + (d.scroll[i] ?? 0)),
    clicks: addCounts(a.clicks, d.clicks), targets: addCounts(a.targets, d.targets, MAX_TARGETS_PER_PAGE),
  };
}
export const emptyPageDay = (day: string, page: string, vp: Viewport): PageDay => ({ day, page, vp, views: 0, scroll: Array(SCROLL_STEPS).fill(0), clicks: {}, targets: {} });

/* ─────────────── reading the pages back ─────────────── */

/** A heatmap cell with fewer clicks than this is not shown ("not enough data yet"), as on the Insights heatmaps. */
export const MIN_CELL_CLICKS = 3;

export interface PageSummary { page: string; views: number; byViewport: Record<Viewport, number>; clicks: number; medianScroll: number | null }
export function pageSummaries(days: readonly PageDay[]): PageSummary[] {
  const m = new Map<string, PageSummary & { hist: number[] }>();
  for (const d of days) {
    const s = m.get(d.page) ?? { page: d.page, views: 0, byViewport: { mobile: 0, tablet: 0, desktop: 0 }, clicks: 0, medianScroll: null, hist: Array(SCROLL_STEPS).fill(0) };
    s.views += d.views; s.byViewport[d.vp] += d.views;
    s.clicks += Object.values(d.clicks).reduce((a, b) => a + b, 0);
    d.scroll.forEach((n, i) => { s.hist[i]! += n; });
    m.set(d.page, s);
  }
  return [...m.values()].map(({ hist, ...s }) => ({ ...s, medianScroll: medianDepth(hist) })).sort((a, b) => b.views - a.views || (a.page < b.page ? -1 : 1));
}
function medianDepth(hist: number[]): number | null {
  const total = hist.reduce((a, b) => a + b, 0);
  if (!total) return null;
  let run = 0;
  for (let i = 0; i < hist.length; i++) { run += hist[i]!; if (run * 2 >= total) return i * 10; }
  return 100;
}

export interface PageDetail {
  page: string; vp: Viewport | "all"; views: number;
  /** reach[i] = percent of measured views that scrolled at least i*10 %. */
  scroll: { measured: number; reach: { depth: number; pct: number }[] };
  heat: {
    cols: number; rowPx: number; rows: number; clicks: number;
    /** Cells with at least MIN_CELL_CLICKS: clicks, and clicks per 100 views (the average). */
    cells: { col: number; row: number; clicks: number; per100: number }[];
    /** Clicks in cells under the minimum (counted, not placed). */
    hiddenClicks: number;
  };
  targets: { name: string; clicks: number; per100: number }[];
}

/** One page over some days: the scroll-depth curve and the click grid, averaged per 100 views. */
export function pageDetail(days: readonly PageDay[], page: string, vp: Viewport | "all"): PageDetail {
  const mine = days.filter((d) => d.page === page && (vp === "all" || d.vp === vp));
  const views = mine.reduce((a, d) => a + d.views, 0);
  const hist = Array<number>(SCROLL_STEPS).fill(0);
  let clicks: Record<string, number> = {}, targets: Record<string, number> = {};
  for (const d of mine) { d.scroll.forEach((n, i) => { hist[i]! += n; }); clicks = addCounts(clicks, d.clicks); targets = addCounts(targets, d.targets); }
  const measured = hist.reduce((a, b) => a + b, 0);
  let atLeast = measured;
  const reach = hist.map((n, i) => { const r = { depth: i * 10, pct: measured ? Math.round((atLeast / measured) * 1000) / 10 : 0 }; atLeast -= n; return r; });
  const per100 = (n: number) => (views ? Math.round((n / views) * 1000) / 10 : 0);
  const all = Object.entries(clicks).map(([k, n]) => { const [c, r] = k.split(":").map(Number); return { col: c!, row: r!, clicks: n, per100: per100(n) }; });
  const shown = all.filter((c) => c.clicks >= MIN_CELL_CLICKS).sort((a, b) => a.row - b.row || a.col - b.col);
  const total = all.reduce((a, c) => a + c.clicks, 0);
  return {
    page, vp, views, scroll: { measured, reach },
    heat: { cols: X_COLS, rowPx: Y_ROW_PX, rows: all.length ? Math.max(...all.map((c) => c.row)) + 1 : 0, clicks: total, cells: shown, hiddenClicks: total - shown.reduce((a, c) => a + c.clicks, 0) },
    targets: Object.entries(targets).map(([name, n]) => ({ name, clicks: n, per100: per100(n) })).sort((a, b) => b.clicks - a.clicks || (a.name < b.name ? -1 : 1)).slice(0, 15),
  };
}
